import { useState, useRef, useEffect, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { ChevronLeft, ChevronRight, Maximize2, X, CheckCircle2, Loader2 } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

export default function PdfViewer({ pdfUrl, completed, onComplete, compact = false }) {
  const canvasRef = useRef(null);
  const docRef = useRef(null);
  const renderTaskRef = useRef(null);
  const frameRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [fs, setFs] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const task = pdfjsLib.getDocument({ url: pdfUrl, withCredentials: true });
    task.promise
      .then((pdf) => {
        if (cancelled) return;
        docRef.current = pdf;
        setNumPages(pdf.numPages);
        setPage(1);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) { setError(true); setLoading(false); console.error("PDF load error", e); }
      });
    return () => { cancelled = true; try { docRef.current?.destroy?.(); } catch {} };
  }, [pdfUrl]);

  const renderPage = useCallback(async (n) => {
    const pdf = docRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return;
    const pg = await pdf.getPage(n);
    let viewport;
    if (fs) {
      const vw = window.innerWidth;
      const vh = window.innerHeight - 64;
      const rotation = portrait ? 90 : 0;
      const base = pg.getViewport({ scale: 1, rotation });
      const scale = Math.min(vw / base.width, vh / base.height);
      viewport = pg.getViewport({ scale, rotation });
    } else {
      const containerW = canvas.parentElement?.clientWidth || 800;
      const base = pg.getViewport({ scale: 1 });
      viewport = pg.getViewport({ scale: containerW / base.width });
    }
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    if (renderTaskRef.current) { try { renderTaskRef.current.cancel(); } catch {} }
    renderTaskRef.current = pg.render({ canvasContext: canvas.getContext("2d"), viewport });
    await renderTaskRef.current.promise.catch(() => {});
  }, [fs, portrait]);

  useEffect(() => {
    if (!loading && numPages) renderPage(page);
  }, [page, loading, numPages, fs, portrait, renderPage]);

  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)");
    const u = () => setPortrait(mq.matches);
    u();
    mq.addEventListener ? mq.addEventListener("change", u) : mq.addListener(u);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", u) : mq.removeListener(u));
  }, []);

  useEffect(() => {
    const h = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setFs(false);
        try { window.screen?.orientation?.unlock?.(); } catch {}
      }
    };
    document.addEventListener("fullscreenchange", h);
    document.addEventListener("webkitfullscreenchange", h);
    return () => {
      document.removeEventListener("fullscreenchange", h);
      document.removeEventListener("webkitfullscreenchange", h);
    };
  }, []);

  const atEnd = page >= numPages;
  const next = () => {
    const n = Math.min(numPages, page + 1);
    setPage(n);
    if (n === numPages && !completed && onComplete) onComplete();
  };
  const prev = () => setPage(Math.max(1, page - 1));

  const enterFs = async () => {
    setFs(true);
    try { if (frameRef.current?.requestFullscreen) await frameRef.current.requestFullscreen(); } catch {}
    try { await window.screen?.orientation?.lock?.("landscape"); } catch {}
  };
  const exitFs = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch {}
    try { window.screen?.orientation?.unlock?.(); } catch {}
    setFs(false);
  };

  if (error) {
    return (
      <div className="text-sm text-[#ba1a1a] p-4 border border-[#E5E7EB] rounded-lg bg-white" data-testid="pdf-error">
        Could not load the presentation. Please try again.
      </div>
    );
  }

  return (
    <div ref={frameRef} data-testid="pdf-viewer">
      {!fs && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
          <div className="relative bg-neutral-100 flex items-center justify-center min-h-[220px]">
            {loading && <Loader2 className="w-6 h-6 animate-spin text-[#003d9b] absolute" />}
            <canvas ref={canvasRef} className="w-full block" data-testid="pdf-canvas" />
            {!compact && !loading && (
              <button onClick={enterFs} data-testid="pdf-fullscreen-btn"
                className="absolute top-2 right-2 bg-white/90 rounded-md px-3 h-9 flex items-center gap-1.5 text-sm font-semibold text-[#1c1b1b] hover:bg-white transition-colors">
                <Maximize2 className="w-4 h-4" /> Present
              </button>
            )}
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-[#E5E7EB]">
            <button onClick={prev} disabled={page <= 1} data-testid="pdf-prev-btn"
              className="flex items-center gap-1 text-sm font-semibold text-[#003d9b] disabled:opacity-40 h-11 px-2">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm font-semibold text-[#434654]" data-testid="pdf-page-counter">
              Page {page} / {numPages || "…"}
            </span>
            {!atEnd ? (
              <button onClick={next} data-testid="pdf-next-btn"
                className="flex items-center gap-1 text-sm font-semibold text-[#003d9b] h-11 px-2">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <span className="flex items-center gap-1 text-sm font-semibold text-[#004e33]" data-testid="pdf-complete-indicator">
                <CheckCircle2 className="w-4 h-4" /> End
              </span>
            )}
          </div>
        </div>
      )}

      {fs && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col select-none" data-testid="pdf-fullscreen">
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <canvas ref={canvasRef} data-testid="pdf-fullscreen-canvas" />
          </div>
          <button onClick={exitFs} data-testid="pdf-exit-fullscreen-btn"
            className="absolute top-4 right-4 z-10 bg-white/90 rounded-full w-11 h-11 flex items-center justify-center hover:bg-white transition-colors">
            <X className="w-5 h-5 text-[#1c1b1b]" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm">
            <button onClick={prev} disabled={page <= 1} data-testid="pdf-fs-prev-btn"
              className="flex items-center gap-1 text-white font-semibold disabled:opacity-40 h-11 px-3">
              <ChevronLeft className="w-5 h-5" /> Prev
            </button>
            <span className="text-white font-semibold text-sm">Page {page} / {numPages}</span>
            {!atEnd ? (
              <button onClick={next} data-testid="pdf-fs-next-btn"
                className="flex items-center gap-1 text-white font-semibold h-11 px-3">
                Next <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <span className="flex items-center gap-1 text-[#5ee0a8] font-semibold h-11 px-3">
                <CheckCircle2 className="w-5 h-5" /> End
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
